'use client';

import { c as _c } from "react/compiler-runtime";
import { Badge, Skeleton } from '@common/components';
import { cn } from '@core/utils';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { CustomLink } from '@/app/_components/CustomLink';
import { Image } from '@/components/client';
import { ROUTES } from '@/constants';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useAutoAttendanceCheckIn } from '../_hooks/useAutoAttendanceCheckIn';
import { useBenefitMenus } from '../_hooks/useBenefitMenus';
import { useOpenOfferwall } from '../_hooks/useOpenOfferwall';
import { findMissionGroupIndex } from '../_utils/resolveBenefitMenuGroups';
import { useLuckyTicketCount } from '../lucky-ticket/_hooks/useLuckyTicketCount';
import { BENEFIT_MISSION_SECTION_ID, BENEFIT_SECTION_CLASS_NAME } from './BenefitMenuSection';
const SECTION_CLASS_NAME = cn(BENEFIT_SECTION_CLASS_NAME, 'gap-16 xl:gap-20');
const HEADER_CLASS_NAME = 'flex flex-col gap-4 xl:flex-row xl:items-center xl:gap-10';
const TILE_ROW_CLASS_NAME = 'flex gap-12';

//* 모바일은 4개가 행을 꽉 채우는 등폭, PC는 시안대로 80px 고정폭이다.
const TILE_CLASS_NAME = 'group relative flex flex-1 flex-col items-center gap-4 xl:flex-none xl:gap-8';

//* 모바일 min-w 없음 — 4타일이 되며 min-w-64×4가 280~320px 커버폰 본문폭을 넘는다. flex-1 등분에 맡긴다.
const ICON_BOX_CLASS_NAME = 'relative flex h-64 w-full items-center justify-center overflow-hidden rounded-small border transition-colors xl:h-76 xl:min-w-80';
const OFFERWALL_ICON_BOX_CLASS_NAME = 'border-Y-400 bg-Y-300 group-focus-visible:bg-Y-400 group-active:bg-Y-400 xl:group-hover:bg-Y-400';

//* PC처럼 스크롤이 일어나지 않아도 클릭 반응이 보이게 목적지 섹션 주변을 잠시 밝힌다.
//* 배경은 덮지 않고 그림자 글로우만: 빠르게 켜져 ~2초 머물고 천천히 사라진다 (Y-500 계열).
//* box-shadow만 전이하므로 레이아웃 시프트가 없다.
const MISSION_HIGHLIGHT_GLOW = '0 0 0 2px rgba(255, 215, 21, 0.4), 0 0 20px 4px rgba(255, 215, 21, 0.3)';
const MISSION_HIGHLIGHT_KEYFRAMES: Keyframe[] = [{
  boxShadow: '0 0 0 0 rgba(255, 215, 21, 0)'
}, {
  boxShadow: MISSION_HIGHLIGHT_GLOW,
  offset: 0.08
}, {
  boxShadow: MISSION_HIGHLIGHT_GLOW,
  offset: 0.6
}, {
  boxShadow: '0 0 0 0 rgba(255, 215, 21, 0)'
}];
const MISSION_HIGHLIGHT_TIMING: KeyframeAnimationOptions = {
  duration: 3500,
  iterations: 1,
  easing: 'ease-in-out'
};
const LuckyTicketBadge = () => {
  const $ = _c(2);
  const {
    data: luckyTicketCount
  } = useLuckyTicketCount();
  if (luckyTicketCount === 0) {
    return null;
  }
  let t0;
  if ($[0] !== luckyTicketCount) {
    t0 = <Badge className="absolute -right-4 -top-8 bg-O-25 text-O-300">{luckyTicketCount}개</Badge>;
    $[0] = luckyTicketCount;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
interface BenefitQuickActionSectionProps {
  isGuest: boolean;
}

/** 혜택 메뉴 첫 그룹의 퀵액션 타일(Figma 10422-109204). */
export const BenefitQuickActionSection = t0 => {
  const $ = _c(32);
  const {
    isGuest
  } = t0;
  const {
    data: groups
  } = useBenefitMenus();
  const {
    openOfferwall,
    gateModals
  } = useOpenOfferwall();
  const t1 = !isGuest;
  let t2;
  if ($[0] !== t1) {
    t2 = {
      enabled: t1
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const {
    isCheckedInToday
  } = useAutoAttendanceCheckIn(t2);
  const isReducedMotion = useReducedMotion();
  const [quickActionGroup] = groups;
  let t3;
  if ($[2] !== groups) {
    t3 = findMissionGroupIndex(groups.slice(1));
    $[2] = groups;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const hasMissionSection = t3 !== -1;
  let t4;
  if ($[4] !== isReducedMotion) {
    t4 = () => {
      const missionSection = document.getElementById(BENEFIT_MISSION_SECTION_ID);
      if (!missionSection) {
        return;
      }
      missionSection.scrollIntoView({
        block: "start"
      });
      if (isReducedMotion) {
        return;
      }
      missionSection.getAnimations().forEach(_temp);
      missionSection.animate(MISSION_HIGHLIGHT_KEYFRAMES, MISSION_HIGHLIGHT_TIMING);
    };
    $[4] = isReducedMotion;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const moveToMissionSection = t4;
  if (!quickActionGroup) {
    return null;
  }
  let t5;
  if ($[6] !== quickActionGroup.title) {
    t5 = <h2 className="font-700 text-Gray-900 typo-body3 xl:text-Gray-700">{quickActionGroup.title}</h2>;
    $[6] = quickActionGroup.title;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="font-500 text-Gray-550 typo-body4">매일 참여 가능해요</p>;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== t5) {
    t7 = <div className={HEADER_CLASS_NAME}>{t5}{t6}</div>;
    $[9] = t5;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== isCheckedInToday || $[12] !== isGuest || $[13] !== openOfferwall || $[14] !== quickActionGroup.items) {
    let t9;
    if ($[16] !== isCheckedInToday || $[17] !== isGuest || $[18] !== openOfferwall) {
      t9 = item => {
        const {
          url,
          placementKey
        } = item;
        const isOfferwall = !url && Boolean(placementKey);
        const isAttendanceTile = url === ROUTES.BENEFIT_ATTENDANCE.pathname;
        const content = <><span className={cn(ICON_BOX_CLASS_NAME, isOfferwall ? OFFERWALL_ICON_BOX_CLASS_NAME : "border-Gray-200 bg-Gray-white group-focus-visible:border-Gray-300 group-focus-visible:bg-Gray-50 group-active:bg-Gray-50 xl:group-hover:border-Gray-300 xl:group-hover:bg-Gray-50")}><Image src={item.imageUrl} alt="" width={56} height={56} sizes="56px" className={cn("relative shrink-0 object-contain", isOfferwall ? "size-56" : "size-36 xl:size-44")} /></span><span className="whitespace-nowrap font-700 text-Gray-900 typo-caption1 xl:typo-body4">{isAttendanceTile && isCheckedInToday ? "\uCD9C\uC11D\uCCB4\uD06C \uC644\uB8CC" : item.title}</span>{!isGuest && url === ROUTES.BENEFIT_SCRATCH.pathname ? <ErrorBoundary fallback={null}><Suspense fallback={null}><LuckyTicketBadge /></Suspense></ErrorBoundary> : null}</>;
        if (url) {
          return <CustomLink key={item.title} href={url} className={TILE_CLASS_NAME}>{content}</CustomLink>;
        }
        if (placementKey) {
          return <button key={item.title} onClick={() => openOfferwall(placementKey)} className={TILE_CLASS_NAME}>{content}</button>;
        }
        return <span key={item.title} aria-disabled="true" className={cn(TILE_CLASS_NAME, "opacity-50")}>{content}</span>;
      };
      $[16] = isCheckedInToday;
      $[17] = isGuest;
      $[18] = openOfferwall;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    t8 = quickActionGroup.items.map(t9);
    $[11] = isCheckedInToday;
    $[12] = isGuest;
    $[13] = openOfferwall;
    $[14] = quickActionGroup.items;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[20] !== hasMissionSection || $[21] !== moveToMissionSection) {
    t9 = hasMissionSection ? <button onClick={moveToMissionSection} className={TILE_CLASS_NAME}><span className={cn(ICON_BOX_CLASS_NAME, OFFERWALL_ICON_BOX_CLASS_NAME)}><Image src="/assets/svgs/wello-point-symbol.svg" alt="" width={56} height={56} sizes="56px" className="relative size-56 shrink-0 object-contain" /></span><span className="whitespace-nowrap font-700 text-Gray-900 typo-caption1 xl:typo-body4">웰포인트 받기</span></button> : null;
    $[20] = hasMissionSection;
    $[21] = moveToMissionSection;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t8 || $[24] !== t9) {
    t10 = <div className={TILE_ROW_CLASS_NAME}>{t8}{t9}</div>;
    $[23] = t8;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  let t11;
  if ($[26] !== t10 || $[27] !== t7) {
    t11 = <section className={SECTION_CLASS_NAME}>{t7}{t10}</section>;
    $[26] = t10;
    $[27] = t7;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  let t12;
  if ($[29] !== gateModals || $[30] !== t11) {
    t12 = <>{t11}{gateModals}</>;
    $[29] = gateModals;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  return t12;
};
export const BenefitQuickActionSectionFallback = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className={HEADER_CLASS_NAME}><Skeleton typo="body3" className="w-172 rounded-xsmall" /><Skeleton typo="body4" className="w-102 rounded-xsmall" /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <section className={SECTION_CLASS_NAME}>{t0}<div className={TILE_ROW_CLASS_NAME}>{[0, 1, 2, 3].map(_temp2)}</div></section>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
function _temp(currentAnimation) {
  return currentAnimation.cancel();
}
function _temp2(tile) {
  return <div key={tile} className={TILE_CLASS_NAME}><Skeleton className="h-64 w-full rounded-small xl:h-76 xl:w-80" /><Skeleton typo="caption1" className="w-56 rounded-xsmall xl:typo-body4" /></div>;
}